import { useEffect, useState } from 'react';
import { ExternalLink, Maximize2, Minus, Plus } from 'lucide-react';

import { Keycap } from '../common/index.jsx';

/**
 * The image, pinned while the annotator works through all three captions.
 *
 * Because one image is one task, this never reloads between captions - which is most of
 * why the image-grouped layout is faster than three separate tasks.
 */
export function ImagePane({ task, showProvenance = true }) {
  const [zoom, setZoom] = useState(1);

  // Reset the zoom when the image changes; carrying it over is disorienting.
  useEffect(() => setZoom(1), [task?.id]);

  if (!task) return null;

  const fitted = zoom === 1;
  const style = fitted
    ? { width: 'auto', maxWidth: '100%', maxHeight: '100%' }
    : { width: `${zoom * 100}%`, maxWidth: 'none', maxHeight: 'none' };

  return (
    <aside className="image-pane">
      <div className={`image-frame ${fitted ? '' : 'zoomed'}`}>
        {task.image_missing ? (
          <div className="image-placeholder">
            <p>
              <strong>Image not available</strong>
            </p>
            <p className="small">
              The file is missing on the server. Tell an admin which image this is; the
              caption text is still correct.
            </p>
          </div>
        ) : (
          <img src={task.image_url} alt={`Image ${task.image_id}`} style={style} draggable={false} />
        )}
      </div>

      <div className="row-between">
        <div className="zoom-bar">
          <button
            type="button"
            className="btn sm icon"
            aria-label="Zoom out"
            onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
          >
            <Minus size={14} />
          </button>
          <span className="zoom-value">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            className="btn sm icon"
            aria-label="Zoom in"
            onClick={() => setZoom((z) => Math.min(8, +(z + 0.25).toFixed(2)))}
          >
            <Plus size={14} />
          </button>
          <button
            type="button"
            className="btn sm"
            onClick={() => setZoom((z) => (z === 1 ? 2 : 1))}
            title="Toggle fit / 2x"
          >
            <Maximize2 size={13} /> <Keycap>z</Keycap>
          </button>
        </div>
        <span className="tiny faint">
          {task.family === 'coco' ? 'COCO' : 'Web'} · {task.image_id}
        </span>
      </div>

      {showProvenance && (task.title || task.source_url) && (
        <div className="image-meta">
          {task.title && <strong>{task.title}</strong>}
          {task.source_domain && <span>{task.source_domain}</span>}
          {task.source_url && (
            <a href={task.source_url} target="_blank" rel="noreferrer noopener">
              Source <ExternalLink size={11} style={{ verticalAlign: 'middle' }} />
            </a>
          )}
        </div>
      )}
    </aside>
  );
}
